// @ts-strict-ignore
import {PureComponent} from 'react';
import moment from 'moment';
import uniqueBy from 'lodash/uniqBy';
import debounce from 'lodash/debounce';
import produce from 'immer';
import React from 'react';
import {clsx} from 'clsx';

import {InputComponent} from 'common/components/input';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {Dashboard} from 'common/report/dashboards/types';
import {confirm} from 'common/components/confirm';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {TIMEZONES} from 'common/constants';
import {SelectedChannelsChangedDetail} from 'common/widgets/slack-channel-selector/types';
import {isValidEmailAddress} from 'common/util/string';
import {KEY_VALUES} from 'common/util/dom';
import {apiClient} from 'common/util/api-client';
import {reporter} from 'common/report/error-reporting';
import {Section} from 'common/components/items-menu/types';
import {ProjectMemberResponse} from 'common/types/app-api/project-member';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {CurrentUser} from 'common/report/model-resources/users';
import {fetchDomainData} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {DomainGetResponse} from 'common/report/queries/app-api/legacy/org-types';
import {react2panel} from 'common/util/react2panel';
import {useCurrentUser} from 'common/contexts/react/current-user-context';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {getDigestApiUrl} from 'common/query-builder/query-report-header/export-dashboard-modal';
import {IronContextRegistry} from 'common/report/context-registry';
import {getLocation} from 'common/util/url';
import {useTranslation} from 'common/i18n/hooks';

import styles from './index.module.styl';
import {CreateOrEditView, ListView} from './template';
import {
  DashboardDigest,
  DashboardDigestSlackSubscription,
  DIGEST_DATE_FORMAT,
  DIGEST_DATE_ORDINAL_FRIENDLY_NAME,
  DIGEST_RECURRENCE_FRIENDLY_NAME,
  DigestDateOrdinal,
  DigestRecurrence,
  ListOrCreateEditView,
  LOADING_STATES,
  LoadingStatus,
  ProjectMember,
  SerializedDashboardDigest,
  SubscribeSendMethod,
} from './types';
import {getDigestTrackingData, isAsapDigest, isEmailDigest, isSlackDigest, responseToDashboardObject} from './util';
import {DIGEST_COPY} from './constants';
import {DigestTrackingEvent, SaveNewDigestUiLocation, track} from './tracking';

const HOURS_OF_THE_DAY = Array.from(Array(24)).map((_, index) => index);

const formatHour = (hour: number) => moment().hour(hour).format(`h A`);

async function getDashboards(projectId: number, workspaceId?: number): Promise<Dashboard[]> {
  try {
    let dashboardResults = null;
    if (workspaceId) {
      dashboardResults = await dashboardsClient.queryAll({workspaceId}).network;
    } else {
      dashboardResults = await dashboardsClient.queryAll({projectId}).network;
    }
    return responseToDashboardObject(dashboardResults).filter((d) => d.can_view);
  } catch (error) {
    reporter.error(error, `Unable to get dashboards for project ${projectId}`);
    throw error;
  }
}

export async function getUsers(projectId?: number): Promise<LoadingState<ProjectMember[]>> {
  try {
    const response = await apiClient.get<ProjectMemberResponse>(`/api/app/projects/${projectId}/granted-users`);
    return {
      loading: LoadingStatus.Success,
      data: uniqueBy<ProjectMember>(
        response.members.map(
          (member): ProjectMember => ({
            id: member.id,
            name: member.name,
            email: member.email,
            role: member.role,
          }),
        ),
        `id`,
      ),
      error: null,
    };
  } catch (err) {
    if (err.status === 403) {
      return {
        loading: LoadingStatus.Success,
        data: [],
        error: null,
      };
    }
    reporter.error(err, `Failed to fetch existing project members`);
    return {
      loading: LoadingStatus.Failed,
      data: [],
      error: err,
    };
  }
}

export type LoadingState<T> = {
  loading: LoadingStatus;
  data: T;
  error: Error;
};

type SubscribeToBoardsMenuState = {
  availableMembers: LoadingState<ProjectMember[]>;
  membersMap: Map<string, ProjectMember>;
  /**
   * The current digest that is being edited. This can be an existing digest or
   * a new one.
   */
  currentDigest: DashboardDigest | null;
  currentTab: SubscribeSendMethod;
  digestIsBeingUpdatedMap: Map<number, boolean>;
  dashboardDigests: LoadingState<DashboardDigest[]>;
  emailDomains: DomainGetResponse[];
  emailSubscribers: Array<Partial<ProjectMember>>;
  isDeletingDigest: boolean;
  isEditDisabled: boolean;
  isSavingDigest: boolean;
  isSendingTestDigest: boolean;
  listOrCreateEditView: ListOrCreateEditView;
  monthlyDateOrdinal: DigestDateOrdinal;
  recurrence: DigestRecurrence;
  subscriberSearchFocused: boolean;
  startDate: moment.Moment;
  selectedTimezone: string;
  slackSubscriptions: DashboardDigestSlackSubscription[];
  subscriberSearch: string;

  // Only used if params.dashboardId is not provided.
  selectedDashboard?: Dashboard;
  dashboards: Dashboard[];
};

type SubscribeToBoardsMenuProps = {
  createDigestCallback?: (digest: DashboardDigest) => void;
  createOnly?: boolean;
  dashboardId?: number;
  deleteDigestCallback?: (digestId: number) => void;
  /**
   * An optional digest to update. This should be provided with createOnly: true
   * to get a view that enables editing a single digest without showing the list
   * of all digests.
   */
  digestToUpdate?: DashboardDigest;
  /**
   * Used to communicate whether the parent has closed this component. Useful
   * for reseting state if someone closes a menu by clicking outside of it for
   * instance.
   */
  isOpen?: boolean;
  members?: LoadingState<ProjectMember[]>;
  onCloseCallback?: () => void;
  projectId: number;
  projectTimezone: string;
  updateDigestCallback?: (digest: DashboardDigest) => void;
  workspaceId?: number;
};

const getDefaultState = (): SubscribeToBoardsMenuState => ({
  availableMembers: {
    loading: LoadingStatus.Idle,
    data: null,
    error: null,
  },
  membersMap: new Map(),
  currentDigest: null,
  currentTab: SubscribeSendMethod.Email,
  dashboardDigests: {
    loading: LoadingStatus.Idle,
    data: null,
    error: null,
  },
  dashboards: [],
  digestIsBeingUpdatedMap: new Map(),
  emailDomains: [],
  emailSubscribers: [],
  isDeletingDigest: false,
  isEditDisabled: false,
  isSavingDigest: false,
  isSendingTestDigest: false,
  listOrCreateEditView: ListOrCreateEditView.List,
  monthlyDateOrdinal: DigestDateOrdinal.First,
  recurrence: DigestRecurrence.Daily,
  // NOTE: There's some weird bug where even though we set a default value for
  // selectedZone on state state.selectedTimezone shows up as undefined.
  selectedTimezone: TIMEZONES[0],
  slackSubscriptions: [],
  startDate: moment(),
  subscriberSearch: ``,
  subscriberSearchFocused: false,
});

class SubscribeToBoardsMenu extends PureComponent<
  SubscribeToBoardsMenuProps & Pick<IronContextRegistry, `currentProject` | `currentUser` | `i18n`>,
  SubscribeToBoardsMenuState
> {
  static defaultProps = {
    isOpen: true,
  };

  state = getDefaultState();

  searchInputElementRef = React.createRef<InputComponent>();

  componentDidUpdate(prevProps) {
    const previousParams = prevProps;
    const newProps = this.props;
    const dashboardIdHasChanged = previousParams.dashboardId !== newProps.dashboardId;
    const projectTimezoneHasChanged = previousParams.projectTimezone !== newProps.projectTimezone;
    const isOpenHasChanged = previousParams.isOpen !== newProps.isOpen;
    if (dashboardIdHasChanged) {
      this.resetAllState();
      this.getDigestsForDashboard(newProps.dashboardId, newProps.projectId, newProps.workspaceId);
    }
    if (projectTimezoneHasChanged) {
      this.setState({selectedTimezone: newProps.projectTimezone});
    }
    // External closure of menu
    if (isOpenHasChanged && !newProps.isOpen) {
      this.resetLocalState();
    }
    // lazy load domain data for embed mode
    if (prevProps.isOpen === false && this.props.isOpen === true && this.state.emailDomains.length === 0) {
      const organizationId = this.props.currentProject.get().value?.organizationId;
      fetchDomainData(organizationId).then((res) => this.setState({emailDomains: res}));
    }
  }

  componentDidMount() {
    this.updateSubscriberSearchFocused = debounce(
      (focused) => this.updateSubscriberSearchFocusedInternal(focused),
      100,
    );

    if (!this.props.projectId) {
      throw new Error(`"projectId" is a required component prop`);
    }

    this.resetAllState();

    if (this.props.members?.data) {
      this.setState({
        availableMembers: this.props.members,
        membersMap: new Map(this.props.members.data.map((x) => [x.email, x])),
      });
    } else if (this.state.availableMembers.loading === LoadingStatus.Idle) {
      getUsers(this.props.projectId).then((res) => {
        this.setState({
          availableMembers: res,
          membersMap: new Map(res.data.map((x) => [x.email, x])),
        });
      });
    }
    if (this.props.digestToUpdate) {
      this.onSelectDigest(this.props.digestToUpdate);
    }
    if (this.props.createOnly) {
      this.fetchDashboards(this.props.projectId, this.props.workspaceId);
    } else {
      this.getDigestsForDashboard(this.props.dashboardId, this.props.projectId, this.props.workspaceId);
    }
  }

  private deleteDigestCallback(digestId: number) {
    if (this.props.deleteDigestCallback) {
      this.props.deleteDigestCallback(digestId);
      return;
    }
    this.setState({currentDigest: null, listOrCreateEditView: ListOrCreateEditView.List});
    this.deleteDigestFromList(digestId);
  }

  private updateSubscriberSearchFocusedInternal = (focused: boolean) => {
    this.setState({
      subscriberSearchFocused: focused,
    });
  };

  /**
   * Debounced version of the subscriber search focused update function.
   *
   * This is required because when we select an item from the items menu there
   * is a delay between the item being selected and the input being blurred. We
   * don't want to prematurely show the error message when the user is still
   * selecting an item.
   */
  private declare updateSubscriberSearchFocused: (focused: boolean) => void;

  private getCurrentUser(): CurrentUser {
    return this.props.currentUser.get().value;
  }

  get currentUserEmail(): string {
    return this.getCurrentUser()?.email;
  }

  canEditDigest(digest: DashboardDigest) {
    return digest.creator_email === this.getCurrentUser()?.email;
  }

  currentUserIsOwnerOfDigest(digest: DashboardDigest): boolean {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return false;
    }
    return digest.creator_id === currentUser.id;
  }

  // Use the param, falling back to the dropdown dashboardId if no param dashboardId is provided
  getDashboardId() {
    return this.props.dashboardId || this.state.selectedDashboard?.id;
  }

  // This isn't great but eventually we'll build a new digests page with a better
  // url.
  get manageDigestsModeUrl() {
    return `${getLocation().pathname}/subscriptions`;
  }

  get hoursOfTheDay() {
    return HOURS_OF_THE_DAY.map(formatHour);
  }

  get timezones() {
    return TIMEZONES;
  }

  get updateSubscriberSearchError(): string {
    const subscriberSearch = this.state.subscriberSearch;
    if (subscriberSearch.length && !isValidEmailAddress(subscriberSearch) && !this.state.subscriberSearchFocused) {
      return `Please enter a valid email address.`;
    }
    return ``;
  }

  get emailDigests(): DashboardDigest[] {
    return this.state.dashboardDigests.data?.filter((d) => isEmailDigest(d)).filter((d) => !isAsapDigest(d)) || [];
  }

  get slackDigests(): DashboardDigest[] {
    return this.state.dashboardDigests.data?.filter((d) => isSlackDigest(d)).filter((d) => !isAsapDigest(d)) || [];
  }

  get emailDigestsCountString(): string {
    if (LOADING_STATES.includes(this.state.dashboardDigests.loading)) {
      return ``;
    }
    return `(${this.emailDigests.length})`;
  }

  get slackDigestsCountString(): string {
    if (LOADING_STATES.includes(this.state.dashboardDigests.loading)) {
      return ``;
    }
    return `(${this.slackDigests.length})`;
  }

  get editButtonsDisabled(): boolean {
    return this.state.isDeletingDigest || this.state.isSavingDigest || this.state.isSendingTestDigest;
  }

  get print() {
    return this.props.i18n.print;
  }

  getMonthlyOrdinalName(ordinal: DigestDateOrdinal): string {
    return this.print(DIGEST_DATE_ORDINAL_FRIENDLY_NAME[ordinal]);
  }

  /**
   * Reset only local state for the component.
   */
  private resetLocalState() {
    this.setState({
      ...this.resetSelectedDigestUpdate(),
      listOrCreateEditView: ListOrCreateEditView.List,
      selectedDashboard: null,
    });
  }

  /**
   * Reset local state and state that is fetched from the server.
   */
  private resetAllState() {
    this.resetLocalState();
    this.setState({
      dashboardDigests: {
        loading: LoadingStatus.Idle,
        data: null,
        error: null,
      },
    });
  }

  updateCurrentTab(tab: SubscribeSendMethod) {
    // If we're creating/editing a digest we need to make sure to clear out the
    // the type-specific fields. E.g. if we switch from email to slack we need
    // to clear the list of email subscribers.
    if (tab === SubscribeSendMethod.Email) {
      this.setState({slackSubscriptions: []});
    } else {
      this.setState({emailSubscribers: []});
    }
    this.setState({
      currentTab: tab,
    });
  }

  updateHourOfDay(hour: number) {
    this.setState({startDate: this.state.startDate.clone().hour(hour)});
  }

  updateSelectedWeeklyDay(day: number) {
    this.setState({startDate: this.state.startDate.clone().day(day)});
  }

  updateMonthlyDateOrdinal(ordinal: DigestDateOrdinal) {
    this.setState({monthlyDateOrdinal: ordinal});
  }

  updateSubscriberSearch(value: string) {
    this.setState({subscriberSearch: value});
  }

  updateTimezone(zone: string) {
    this.setState({selectedTimezone: zone});
    this.track(DigestTrackingEvent.ChangedTimezone, this.getSerializedDigestFromCurrentState(), {timezone: zone});
  }

  /**
   * Sets the current digest being edited. Also adds this digest to the list of
   * digests.
   * @param emailDigest
   */
  private updateDigestState(emailDigest: DashboardDigest) {
    this.setState({currentDigest: emailDigest});
    this.props.updateDigestCallback?.(emailDigest);
    // If we're in the createOnly flow we don't have a list of digests to update.
    if (!this.props.createOnly) {
      this.setState(
        produce((draft: SubscribeToBoardsMenuState) => {
          const updatedDigestIndex = draft.dashboardDigests.data.findIndex((x) => x.id === emailDigest.id);
          if (updatedDigestIndex > -1) {
            draft.dashboardDigests.data[updatedDigestIndex] = emailDigest;
          } else {
            draft.dashboardDigests.data.push(emailDigest);
          }
        }),
      );
    }
  }

  async fetchDashboards(projectId?: number, workspaceId?: number) {
    try {
      const dashboards = await getDashboards(projectId, workspaceId);
      this.setState({dashboards});
    } catch (error) {
      reporter.error(error);
    }
  }

  /**
   * Generates a serialized digest expected by the API from the current component
   * state.
   * @param recurrence Optionally override the current recurrence value. This
   * enables us to send ASAP digests without having to update the state.
   * @returns
   */
  private getSerializedDigestFromCurrentState(
    recurrence: DigestRecurrence = this.state.recurrence,
  ): SerializedDashboardDigest {
    const startDate =
      recurrence === DigestRecurrence.ASAP ? this.state.startDate : this.state.startDate.clone().startOf(`hour`);
    return {
      monthly_week_ordinal: this.state.monthlyDateOrdinal,
      recur: recurrence,
      start_date: startDate.format(DIGEST_DATE_FORMAT), // eslint-disable-line camelcase
      timezone: this.state.selectedTimezone,
      slack_subscriptions: this.state.slackSubscriptions,
      recipients: this.state.emailSubscribers.map((sub) => sub.email),
      dashboard_id: this.getDashboardId(),
    };
  }

  private handleDigestApiError(err: any, toastMessage: string): void {
    reporter.error(err);
    this.setState(
      produce((draft) => {
        draft.dashboardDigests.error = err;
        return draft;
      }),
    );
    let message = toastMessage || err.message;
    // Special case for when the user doesn't have permission to view the board.
    if (err.message === `User does not have permission to view board`) {
      message = `You cannot create a subscription for this board because you do not have permission to view the board.`;
    }
    addToast({
      message,
      type: ToastType.ERROR,
    });
  }

  /**
   * Takes a serialized digest and sends it to the API to create a new digest.
   * @param serializedDigest The serialized digest that the API expects.
   * @returns A promise that resolves to the newly created digest.
   */
  private createDigestApiCall(serializedDigest: SerializedDashboardDigest) {
    const promise = apiClient.post<DashboardDigest>(
      getDigestApiUrl(this.props.projectId, this.props.workspaceId),
      serializedDigest,
    );
    promise.catch((err) => this.handleDigestApiError(err, this.print(DIGEST_COPY.createDigestError)));
    return promise;
  }

  private track(
    event: DigestTrackingEvent,
    digest?: Partial<DashboardDigest> | Partial<SerializedDashboardDigest>,
    data: Record<string, any> = {},
  ) {
    track(event, {
      isNewSubscriptionsUi: true,
      // Right now this is a 1:1 match but this could potentially become incorrect
      'ui location': this.props.createOnly
        ? SaveNewDigestUiLocation.SubscriptionsPage
        : SaveNewDigestUiLocation.SubscribeButtonMenu,
      ...data,
      ...(digest ? getDigestTrackingData(digest, this.state.membersMap, this.props.i18n) : {}),
    });
  }

  // TODO: Workaround for panel bombing in Storybook. Should be able to use
  // default prop when converting this to React.
  createDigestCallback = (digest: DashboardDigest) => {
    if (this.props.createDigestCallback) {
      this.props.createDigestCallback(digest);
    } else {
      this.updateDigestState(digest);
    }
  };

  /**
   * Create a new digest and handles updating the list of digests and setting
   * the currently edited/viewed digest to the newly created digest.
   */
  createDigest() {
    this.setState({isSavingDigest: true});
    this.createDigestApiCall(this.getSerializedDigestFromCurrentState())
      .then((res) => {
        this.track(DigestTrackingEvent.SaveNewDigest, res);
        // Used in the createOnly flow for use in other components
        this.createDigestCallback(res);
        addToast({
          message: DIGEST_COPY.createDigestSuccess,
          type: ToastType.SUCCESS,
        });

        this.closeMenu();
      })
      .finally(() => this.setState({isSavingDigest: false}));
  }

  private closeMenu() {
    // When we close the menu we should also reset it.
    this.resetLocalState();
    this.props.onCloseCallback?.();
  }

  private getDigest(digestId: number): DashboardDigest {
    // If we're passed in a digestToUpdate always default to using that.
    return this.props.digestToUpdate ?? this.state.dashboardDigests.data.find((d) => d.id === digestId);
  }

  /**
   * Patches the given digest. By default it will patch the digest given the state
   * of the component.
   * @param digestId
   * @param updatedDigest If passed in will patch the given digest with this
   * instead of using the component's current state.
   */
  private async patchDigest(
    digestId: number,
    updatedDigest: Partial<SerializedDashboardDigest> = this.getSerializedDigestFromCurrentState(),
  ): Promise<void> {
    this.setState({isSavingDigest: true});

    const digest = this.getDigest(digestId);
    const projectId = digest?.project_id || this.props.projectId;
    const workspaceId = digest?.workspace_id || this.props.workspaceId;
    return apiClient
      .patch<DashboardDigest>(getDigestApiUrl(projectId, workspaceId, digestId.toString()), updatedDigest)
      .then((res) => this.updateDigestState(res))
      .then(() => {
        addToast({message: DIGEST_COPY.updateDigestSuccess, type: ToastType.SUCCESS});
        this.track(DigestTrackingEvent.EditDigest, {...this.getSerializedDigestFromCurrentState(), ...updatedDigest});
      })
      .catch((err) => this.handleDigestApiError(err, this.print(DIGEST_COPY.updateDigestError)))
      .finally(() => this.setState({isSavingDigest: false}));
  }

  /**
   * Patch from the edit screen which will check if the edit will effectively
   * delete the digest. Confirms with the user if they want to delete it in this
   * case.
   * @param digestId
   * @param updatedDigest
   * @returns
   */
  async patchDigestFromEditScreen(
    digestId: number,
    updatedDigest: Partial<SerializedDashboardDigest> = this.getSerializedDigestFromCurrentState(),
  ): Promise<void> {
    const originalSubscriberCount = this.getDigest(digestId)?.recipients.length;
    // If the user tries to update the digest but it now has 0 email subscribers we
    // will delete the digest instead. Prompt the user to confirm this decision.
    // (Make sure we don't count Slack digests invadvertently.)
    if (originalSubscriberCount > 0 && this.state.emailSubscribers.length === 0) {
      if (
        await confirm(
          {
            title: this.print(DIGEST_COPY.deleteDigestTitle),
            theme: ConfirmTheme.DELETE,
          },
          {},
          {
            description: DIGEST_COPY.removingFinalSubscriberWillDeleteDigestPrompt,
            confirmButtonLabel: ({t}) => t(`common.delete`),
          },
        )
      ) {
        await this.deleteDigest(digestId);
        this.closeMenu();
      }
      return;
    }

    return this.patchDigest(digestId, updatedDigest).then(() => {
      this.closeMenu();
    });
  }

  private deleteDigestFromList(digestId: number) {
    // If we're in the createOnly flow we don't have a list of digests to update.
    if (!this.state.dashboardDigests.data) {
      return;
    }
    this.setState(
      produce((draft: SubscribeToBoardsMenuState) => {
        const indexOf = draft.dashboardDigests.data.findIndex((d) => d.id === digestId);
        draft.dashboardDigests.data.splice(indexOf, 1);
      }),
    );
  }

  async onDeleteDigest(digestId: number) {
    const digest = this.getDigest(digestId);

    const digestType = digest?.recipients.length ? SubscribeSendMethod.Email : SubscribeSendMethod.Slack;
    const subscriptionCount =
      digestType === SubscribeSendMethod.Email
        ? digest?.recipients.length || 0
        : digest?.slack_subscriptions.length || 0;

    if (
      await confirm(
        {
          title: this.print(DIGEST_COPY.deleteDigestTitle),
          description:
            digestType === SubscribeSendMethod.Email
              ? this.props.i18n.t(`subscriptions.delete-email`, {count: subscriptionCount})
              : this.props.i18n.t(`subscriptions.delete-slack`, {count: subscriptionCount}),
          theme: ConfirmTheme.DELETE,
        },
        {},
        {
          confirmButtonLabel: ({t}) => t(`common.delete`),
        },
      )
    ) {
      this.deleteDigest(digestId, digest?.project_id, digest?.workspace_id).then(() => this.closeMenu());
    }
  }

  private async deleteDigest(digestId: number, projectId?: number, workspaceId?: number) {
    this.setState({isDeletingDigest: true});
    return apiClient
      .delete<DashboardDigest>(
        getDigestApiUrl(projectId || this.props.projectId, workspaceId || this.props.workspaceId, digestId.toString()),
      )
      .then(() => {
        this.track(DigestTrackingEvent.DeleteDigest, this.getDigest(digestId));
        this.deleteDigestCallback(digestId);
        addToast({
          message: DIGEST_COPY.deleteDigestSuccess,
          type: ToastType.DESTRUCTIVE,
        });
      })
      .catch((err) => this.handleDigestApiError(err, this.print(DIGEST_COPY.deleteDigestError)))
      .finally(() => this.setState({isDeletingDigest: false}));
  }

  currentUserIsAlreadySubscribedToDigest(digestId: number): boolean {
    return this.getDigest(digestId)?.recipients.includes(this.currentUserEmail);
  }

  async getDigestsForDashboard(dashboardId: number, projectId: number, workspaceId: number): Promise<void> {
    this.setState(
      produce((draft: SubscribeToBoardsMenuState) => {
        draft.dashboardDigests.loading = LoadingStatus.Pending;
      }),
    );
    try {
      const digests = await apiClient.get<DashboardDigest[]>(
        getDigestApiUrl(projectId, workspaceId) + `?dashboard_id=${dashboardId}`,
      );

      // If there's no existing digests to display, default them to the create
      // view so they can create a new digest.
      if (!digests.length) {
        this.setState({
          listOrCreateEditView: ListOrCreateEditView.CreateEdit,
        });
      }
      this.setState(
        produce((draft: SubscribeToBoardsMenuState) => {
          draft.dashboardDigests.loading = LoadingStatus.Success;
          draft.dashboardDigests.data = digests;
          draft.digestIsBeingUpdatedMap = new Map();
        }),
      );
    } catch (err) {
      reporter.error(err, `Failed to fetch digests`);
      this.setState(
        produce((draft: SubscribeToBoardsMenuState) => {
          draft.dashboardDigests.loading = LoadingStatus.Failed;
          draft.dashboardDigests.error = err;
        }),
      );
    }
  }

  getSections(): Section {
    const availableMembers = this.state.availableMembers;
    if (availableMembers.loading !== LoadingStatus.Success) {
      return {isLoading: true, items: []};
    }

    const subscribersSet = new Set<string>(this.state.emailSubscribers.map((x) => x.email));

    return {
      isLoading: false,
      items: availableMembers.data
        .filter((x) => !subscribersSet.has(x.email))
        .map((member) => ({
          label: `${member.name} <${member.email}>`,
          value: member,
        })),
    };
  }

  getDashboardSections(): Section {
    return {
      isLoading: false,
      items: this.state.dashboards.map((dashboard) => ({
        label: dashboard.title,
        value: dashboard,
      })),
    };
  }

  onBlur() {
    this.updateSubscriberSearchFocused(false);
  }

  onFocus() {
    this.updateSubscriberSearchFocused(true);
  }

  onClickMember(member: ProjectMember) {
    this.setState({...this.addSubscriberUpdate(member), subscriberSearch: ``});
  }

  onClickDashboard(dashboard: Dashboard) {
    this.setState({selectedDashboard: dashboard});
  }

  private addSubscriberUpdate(
    subscriber: Partial<ProjectMember>,
  ): Pick<SubscribeToBoardsMenuState, `emailSubscribers`> {
    // Don't add duplicates
    if (this.state.emailSubscribers.map((x) => x.email).includes(subscriber.email)) {
      return {emailSubscribers: this.state.emailSubscribers};
    }
    // Push new ones to the front of the array because the full list might
    // be cut off and we want the user to see their changes as they're made.
    return {emailSubscribers: [subscriber, ...this.state.emailSubscribers]};
  }

  removeSubscriber(index: number) {
    const newSubscribers = [...this.state.emailSubscribers];
    newSubscribers.splice(index, 1);
    this.setState({emailSubscribers: newSubscribers});
  }

  /**
   * Handles the user hitting the "Create New" button.
   */
  onCreateNewDigest() {
    this.setState({
      ...this.resetSelectedDigestUpdate(),
      listOrCreateEditView: ListOrCreateEditView.CreateEdit,
    });
  }

  onNavBackButton() {
    this.setState({
      currentDigest: null,
      listOrCreateEditView: ListOrCreateEditView.List,
    });
  }

  onSubscriberSearchKeydown(ev: KeyboardEvent) {
    const emailSearch = this.state.subscriberSearch;
    if (
      ev.code === KEY_VALUES.ENTER &&
      emailSearch.length &&
      !this.updateSubscriberSearchError.length &&
      isValidEmailAddress(emailSearch)
    ) {
      this.setState({
        ...this.addSubscriberUpdate({email: emailSearch}),
        subscriberSearch: ``,
      });
    }
  }

  onSelectDigest(digest: DashboardDigest) {
    this.setState({
      currentDigest: digest,
      emailSubscribers: digest.recipients.map((email) => ({email})),
      listOrCreateEditView: ListOrCreateEditView.CreateEdit,
      monthlyDateOrdinal: digest.monthly_week_ordinal,
      recurrence: digest.recur,
      selectedTimezone: digest.timezone,
      slackSubscriptions: digest.slack_subscriptions,
      startDate: moment(digest.start_date),
    });
  }

  private resetSelectedDigestUpdate(): Pick<
    SubscribeToBoardsMenuState,
    | `currentDigest`
    | `emailSubscribers`
    | `slackSubscriptions`
    | `monthlyDateOrdinal`
    | `recurrence`
    | `selectedTimezone`
    | `startDate`
  > {
    return {
      currentDigest: null,
      emailSubscribers: [],
      slackSubscriptions: [],
      monthlyDateOrdinal: DigestDateOrdinal.First,
      recurrence: DigestRecurrence.Daily,
      selectedTimezone: this.props.projectTimezone,
      startDate: moment(),
    };
  }

  onSelectFrequency(option: DigestRecurrence) {
    const originalRecurrence = this.state.recurrence;
    this.setState({recurrence: option});
    this.track(
      // sic, we track recurrence changes with an event called "Changed frequency"
      DigestTrackingEvent.ChangedFrequency,
      this.getSerializedDigestFromCurrentState(),
      {originalRecurrence: this.props.i18n.print(DIGEST_RECURRENCE_FRIENDLY_NAME[originalRecurrence], `en`)},
    );
  }

  /**
   * Creates a new ASAP digest. Does not update the current digest being edited
   * because we don't want to expose ASAP digests to the user we merely want to
   * send them and forget about them.
   */
  async onSendTest() {
    const digestToSend = this.getSerializedDigestFromCurrentState(DigestRecurrence.ASAP);
    this.track(DigestTrackingEvent.SendTestSubscriptionButton, digestToSend);
    const isEmailDigestTab = this.state.currentTab === SubscribeSendMethod.Email;
    if (
      await confirm(
        {
          theme: ConfirmTheme.CONFIRM,
        },
        {},
        {
          title: ({t}) => t(`subscriptions.send-test`),
          description: isEmailDigestTab
            ? DIGEST_COPY.sendTestDigestEmailConfirmation
            : DIGEST_COPY.sendTestDigestSlackConfirmation,
          confirmButtonLabel: ({t}) => t(`subscriptions.proceed-test`),
        },
      )
    ) {
      this.setState({isSendingTestDigest: true});
      this.createDigestApiCall(digestToSend)
        .then((res) => {
          this.track(DigestTrackingEvent.SaveNewDigest, res);
          addToast({
            message: isEmailDigestTab ? DIGEST_COPY.sendTestDigestEmailSuccess : DIGEST_COPY.sendTestDigestSlackSuccess,
            type: ToastType.SUCCESS,
          });
          this.closeMenu();
        })
        .finally(() => this.setState({isSendingTestDigest: false}));
    }
  }

  onSlackSubscriptionsChanged(detail: SelectedChannelsChangedDetail) {
    const selectedChannels = detail.selectedChannels;
    const slackTeamId: string = detail.teamId;
    this.setState({
      slackSubscriptions: selectedChannels.map((channel) => {
        return {slack_channel_name: channel.name, slack_channel_id: channel.id, slack_team_id: slackTeamId};
      }),
    });
  }

  /**
   * Adds or removes the current user from the list of email subscribers for a
   * digest.
   * NOTE: If the user is removing themselves and is the last subscriber this
   * will prompt the user to delete the digest.
   * @param digest
   * @param currentUserIsSubscribedToDigest True if the user is currently subbed
   * to the digest.
   */
  async onSubscribeToExistingDigest(digest: DashboardDigest, currentUserIsSubscribedToDigest: boolean) {
    this.track(DigestTrackingEvent.SubscriptionInlineSubUnsubButton, digest, {
      'sub/unsub': currentUserIsSubscribedToDigest ? `unsubscribe` : `subscribe`,
    });

    const setUpdatingState = (isUpdating: boolean) => {
      this.setState(
        produce((draft: SubscribeToBoardsMenuState) => {
          draft.digestIsBeingUpdatedMap.set(digest.id, isUpdating);
        }),
      );
    };

    // If they are the last subscriber and they want to remove themselves we
    // will instead delete the digest. We need to confirm this is what the user
    // wants.
    if (currentUserIsSubscribedToDigest && digest.recipients.length === 1) {
      if (
        await confirm(
          {
            title: this.print(DIGEST_COPY.deleteDigestTitle),
            theme: ConfirmTheme.DELETE,
          },
          {},
          {
            description: DIGEST_COPY.removingFinalSubscriberWillDeleteDigestPrompt,
            confirmButtonLabel: ({t}) => t(`common.delete`),
          },
        )
      ) {
        setUpdatingState(true);
        await this.deleteDigest(digest.id);
        setUpdatingState(false);
      }
      return;
    }

    setUpdatingState(true);
    const updatedRecipients = currentUserIsSubscribedToDigest
      ? digest.recipients.filter((email) => email !== this.currentUserEmail)
      : digest.recipients.concat(this.currentUserEmail);
    await this.patchDigest(digest.id, {recipients: updatedRecipients});
    setUpdatingState(false);
  }

  render() {
    return (
      <div
        elref={`subscribe-to-boards-menu`}
        aria-hidden={!this.props.isOpen}
        className={clsx(styles.subscribeToBoardsMenu, {
          [styles.navPresent]: !this.props.createOnly,
        })}
      >
        {this.state.listOrCreateEditView === ListOrCreateEditView.CreateEdit || this.props.createOnly ? (
          <CreateOrEditView cmp={this} />
        ) : (
          <ListView cmp={this} />
        )}
      </div>
    );
  }
}

export type {SubscribeToBoardsMenu};

export const SubscribeToBoardsMenuReact = (props: SubscribeToBoardsMenuProps) => {
  const currentProject = useCurrentProject();
  const currentUser = useCurrentUser();
  const i18n = useTranslation();

  return <SubscribeToBoardsMenu {...props} currentProject={currentProject} currentUser={currentUser} i18n={i18n} />;
};

export const SubscribeToBoardsMenuEl = react2panel(`subscribe-to-boards-menu`, SubscribeToBoardsMenuReact);
